import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { Children } from "../../../../../../../../components/miscellianous/children"
import { BSBuilding } from "../../../../../../../../core/dto/beem-shot/BSBuilding/BSBuilding"
import { useBSBuilding } from "../../../../../../../../core/hooks/beem-shot/useBSBuilding"
import { CalculStatusEnum } from "../../../../../../../../core/enum/calculStatusEnum"

export const BSBuildingListContext = React.createContext<BSBuildingListStore>({} as BSBuildingListStore)

export function BSBuildingListContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { bsVariantId } = useParams()

  const { getAllBuilding } = useBSBuilding()

  const [bsBuildingList, setBSBuildingList] = useState<BSBuilding[]>([])
  const [isBSBuildingListLoaded, setIsBSBuildingListLoaded] = useState<boolean>(false)
  const [isBSBuildingListLoading, setIsBSBuildingListLoading] = useState<boolean>(true)

  const refreshBSBuildingList: (variantId: string) => void = useCallback(
    (variantId) => {
      getAllBuilding(variantId).then((newBSBuildingList) => {
        setBSBuildingList(newBSBuildingList)
        setIsBSBuildingListLoaded(true)
        setIsBSBuildingListLoading(false)
      })
    },
    [getAllBuilding]
  )

  useEffect(() => {
    if (bsVariantId) {
      refreshBSBuildingList(bsVariantId)
    } else {
      setBSBuildingList([])
    }
  }, [bsVariantId, refreshBSBuildingList])

  const updateOneBSBuildingStatusState: (bsBuildingId: string, calculStatus: CalculStatusEnum) => void = useCallback(
    (bsBuildingId, calculStatus) => {
      setBSBuildingList((prev) =>
        prev.map((bsBuilding: BSBuilding) => {
          if (bsBuilding.id === bsBuildingId) {
            return { ...bsBuilding, calculStatus }
          } else {
            return bsBuilding
          }
        })
      )
    },
    []
  )

  const updateAllBSBuildingStatusState: (calculStatus: CalculStatusEnum) => void = useCallback((calculStatus) => {
    setBSBuildingList((prev) =>
      prev.map((bsBuilding: BSBuilding) => ({
        ...bsBuilding,
        calculStatus,
      }))
    )
  }, [])

  const bsBuildingStore = useMemo(
    () => ({
      isBSBuildingListLoaded,
      isBSBuildingListLoading,
      bsBuildingList,
      setBSBuildingList,
      refreshBSBuildingList,
      updateOneBSBuildingStatusState,
      updateAllBSBuildingStatusState,
    }),
    [
      isBSBuildingListLoaded,
      isBSBuildingListLoading,
      bsBuildingList,
      refreshBSBuildingList,
      updateOneBSBuildingStatusState,
      updateAllBSBuildingStatusState,
    ]
  )
  return <BSBuildingListContext.Provider value={bsBuildingStore}>{children}</BSBuildingListContext.Provider>
}

export type BSBuildingListStore = {
  isBSBuildingListLoaded: boolean
  isBSBuildingListLoading: boolean
  bsBuildingList: BSBuilding[]
  setBSBuildingList: React.Dispatch<React.SetStateAction<BSBuilding[]>>
  refreshBSBuildingList(variantId: string): void
  updateOneBSBuildingStatusState(bsBuildingId: string, calculStatus: CalculStatusEnum): void
  updateAllBSBuildingStatusState(calculStatus: CalculStatusEnum): void
}
